// src/screens/AdminAmenidadesScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebaseConfig';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { IoArrowBack, IoInformationCircleOutline, IoTimeOutline, IoPeopleOutline, IoWalletOutline, IoSaveOutline, IoImagesOutline, IoLeafOutline, IoClose, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { useAuth } from '../context/AuthContext'; 
import AIAssistantButton from '../components/Shared/AIAssistantButton';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import './AdminAmenidadesScreen.css';

function AdminAmenidadesScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth(); // Get userData
  const { amenidadId } = useParams(); // ¡NUEVO: Leemos el ID de la URL!
  const isEditMode = Boolean(amenidadId); // ¡NUEVO: Determinamos si estamos editando!

  const [enviando, setEnviando] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [successModal, setSuccessModal] = useState({ show: false, message: "" });

  // --- Estados para TODAS las reglas ---
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fotoArchivo, setFotoArchivo] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoUrlActual, setFotoUrlActual] = useState(''); // ¡NUEVO: Para guardar la URL existente!

  // Reglas de Horario
  const [horarioApertura, setHorarioApertura] = useState('09:00');
  const [horarioCierre, setHorarioCierre] = useState('21:00');
  const [duracionBloque, setDuracionBloque] = useState(60);
  const [horasAnticipacion, setHorasAnticipacion] = useState(24);

  // Reglas de Capacidad
  const [capacidadSimultanea, setCapacidadSimultanea] = useState(1);
  const [limiteUsuariosPorReserva, setLimiteUsuariosPorReserva] = useState(10);

  // Reglas de Límites
  const [limiteReservas, setLimiteReservas] = useState(1);
  const [limitePeriodoNum, setLimitePeriodoNum] = useState(1);
  const [limitePeriodoTipo, setLimitePeriodoTipo] = useState('Semana');

  // Reglas Financieras y de Acceso
  const [requiereDeposito, setRequiereDeposito] = useState(false);
  const [montoDeposito, setMontoDeposito] = useState(0);
  const [etiquetasAcceso, setEtiquetasAcceso] = useState([]);
  const [nuevaEtiqueta, setNuevaEtiqueta] = useState('');

  // --- ¡NUEVO: useEffect para cargar datos en Modo Edición! ---
  useEffect(() => {
    if (isEditMode && amenidadId) {
      setLoadingData(true);
      const cargarDatosAmenidad = async () => {
        try {
          const docRef = doc(db, "amenidades", amenidadId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data = docSnap.data();

            // Llenamos todos los estados con los datos existentes
            setNombre(data.nombre || '');
            setDescripcion(data.descripcion || '');
            setFotoUrlActual(data.fotoUrl || ''); // Guardamos la URL de la foto
            setFotoPreview(data.fotoUrl || null); // La mostramos en la vista previa

            // Llenamos las reglas
            const reglas = data.reglas || {};
            setHorarioApertura(reglas.horarioApertura || '09:00');
            setHorarioCierre(reglas.horarioCierre || '21:00');
            setDuracionBloque(reglas.duracionBloque || 60);
            setHorasAnticipacion(reglas.horasAnticipacion || 24);
            setCapacidadSimultanea(reglas.capacidadSimultanea || 1);
            setLimiteUsuariosPorReserva(reglas.limiteUsuariosPorReserva || 10);
            setLimiteReservas(reglas.limiteReservas || 1);
            setLimitePeriodoNum(reglas.limitePeriodoNum || 1);
            setLimitePeriodoTipo(reglas.limitePeriodoTipo || 'Semana');
            setRequiereDeposito(reglas.requiereDeposito || false);
            setMontoDeposito(reglas.montoDeposito || 0);

            // Migración de datos viejos (derechoAcceso: string[]) a etiquetasAcceso
            if (data.derechoAcceso && Array.isArray(data.derechoAcceso) && data.derechoAcceso[0] !== 'todos') {
                setEtiquetasAcceso(data.derechoAcceso);
            } else if (data.derechosAcceso && Array.isArray(data.derechosAcceso)) {
                setEtiquetasAcceso(data.derechosAcceso);
            } else {
                setEtiquetasAcceso([]);
            }

          } else {
            alert("Error: No se encontró la amenidad para modificar.");
            navigate('/gestion-amenidades'); // Regresar a la lista
          }
        } catch (e) {
          console.error("Error al cargar datos: ", e);
          alert("Error al cargar los datos de la amenidad.");
        } finally {
          setLoadingData(false);
        }
      };

      cargarDatosAmenidad();
    }
  }, [isEditMode, amenidadId, navigate]);


  // --- Manejador de la Foto (sin cambios) ---
  const handleFotoChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setFotoArchivo(file);
      setFotoPreview(URL.createObjectURL(file));
    }
  };

  // --- ¡MODIFICADO: handleSubmit ahora maneja Crear y Actualizar! ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre) { alert('El nombre es obligatorio'); return; }
    setEnviando(true);

    try {
      let fotoUrlParaGuardar = fotoUrlActual; // Empezamos con la foto que ya tenía

      // --- PASO 1: Subir la foto (solo si se seleccionó un NUEVO archivo) ---
      if (fotoArchivo) {
        const safeName = fotoArchivo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storageRef = ref(storage, `fotos_amenidades/${Date.now()}_${safeName}`);
        const snapshot = await uploadBytes(storageRef, fotoArchivo);
        fotoUrlParaGuardar = await getDownloadURL(snapshot.ref);
      }

      // --- PASO 2: Preparar los datos ---
      const datosAmenidad = {
        condominioId: userData?.condominioId || 'CONDO_XYZ',
        nombre: nombre,
        descripcion: descripcion,
        fotoUrl: fotoUrlParaGuardar,

        reglas: {
          horarioApertura: horarioApertura,
          horarioCierre: horarioCierre,
          duracionBloque: Number(duracionBloque),
          horasAnticipacion: Number(horasAnticipacion),

          capacidadSimultanea: Number(capacidadSimultanea),
          limiteUsuariosPorReserva: Number(limiteUsuariosPorReserva),

          limiteReservas: Number(limiteReservas),
          limitePeriodoNum: Number(limitePeriodoNum),
          limitePeriodoTipo: limitePeriodoTipo,

          requiereDeposito: requiereDeposito,
          montoDeposito: requiereDeposito ? Number(montoDeposito) : 0,
        },
        derechosAcceso: etiquetasAcceso
      };

      // --- PASO 3: Guardar en Firestore (Crear o Actualizar) ---
      if (isEditMode) {
        // --- MODO EDICIÓN: Actualizamos el documento ---
        const docRef = doc(db, 'amenidades', amenidadId);
        await updateDoc(docRef, datosAmenidad);
        setSuccessModal({ show: true, message: `¡Amenidad "${nombre}" actualizada con éxito!` });

      } else {
        // --- MODO CREACIÓN: Creamos uno nuevo ---
        const amenidadesRef = collection(db, 'amenidades');
        // ¡IMPORTANTE! Añadimos el nuevo campo de bloqueo
        await addDoc(amenidadesRef, {
          ...datosAmenidad,
          bloqueadaPorAdmin: false // ¡VALOR INICIAL!
        });
        setSuccessModal({ show: true, message: `¡Amenidad "${nombre}" creada con éxito!` });
      }

    } catch (error) {
      console.error("Error al guardar amenidad: ", error);
      alert('Error al guardar la amenidad.');
    } finally {
      setEnviando(false);
    }
  };


  if (loadingData) {
    return (
      <div className="ks-page">
        <KondoSidebar activeRoute="/gestion-amenidades" />

        <main className="ks-main">
          <KondoBrandHeader showBack={false} />

          <div className="ks-scroll-area">
            <div className="admin-amenidad-page">
              <div className="admin-amenidad-wrapper" style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                <p style={{ color: '#64748b', fontSize: '1.1rem' }}>Cargando información de la amenidad...</p>
              </div>
            </div>
          </div>
        </main>

        <KondoBottomNav activeRoute="/gestion-amenidades" />
      </div>
    );
  }

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-amenidades" />

      <main className="ks-main">
        <KondoBrandHeader showBack={false} />

        <div className="ks-scroll-area">
          <div className="admin-amenidad-page">
            <div className="admin-amenidad-wrapper">
              
              {/* --- PREMIUM COMPACT BANNER --- */}
              <div className="usuarios-fixed-wrapper">
                  <div className="usuarios-banner-compact" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', boxShadow: '0 4px 15px rgba(124, 58, 237, 0.4)' }}>
                    <button type="button" onClick={() => navigate('/gestion-amenidades')} className="usuarios-back-btn glass">
                      <IoArrowBack size={20} />
                    </button>
                    <div className="usuarios-banner-content">
                      <h2>{isEditMode ? 'Configurar Amenidad' : 'Nueva Amenidad'}</h2>
                      <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>{isEditMode ? 'Edita las reglas, aforos y detalles del espacio.' : 'Crea un nuevo espacio reservable para los residentes.'}</p>
                    </div>
                    <IoLeafOutline className="usuarios-banner-icon" />
                  </div>
              </div>

              <form onSubmit={handleSubmit} className="amenity-form-grid" style={{ marginTop: '20px' }}>

                {/* --- Sección de Información General --- */}
                <div className="amenity-card-section">
                  <div className="section-title">
                    <div className="section-icon"><IoInformationCircleOutline size={22} /></div>
                    Información del Espacio
                  </div>
                  
                  <div className="premium-group" style={{ marginBottom: '20px' }}>
                    <label className="premium-label">Nombre de la Amenidad</label>
                    <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} className="premium-input" placeholder="Ej. Asador Principal, Salón de Eventos..." required />
                  </div>
                  
                  <div className="premium-group" style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label className="premium-label" style={{ margin: 0 }}>Descripción</label>
                      <AIAssistantButton 
                        textoOriginal={descripcion}
                        onSugerenciaAceptada={(nuevoTexto) => setDescripcion(nuevoTexto)}
                        contexto="amenidades"
                      />
                    </div>
                    <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} className="premium-textarea" placeholder="Reglas generales, ubicación, inventario disponible..." />
                  </div>
                  
                  <div className="premium-group">
                    <label className="premium-label">Fotografía de Portada</label>
                    <div className="file-upload-wrapper">
                      <label className="btn-upload">
                        <IoImagesOutline size={20} />
                        Seleccionar Imagen
                        <input type="file" accept="image/*" onChange={handleFotoChange} className="file-upload-input" />
                      </label>
                      {(fotoPreview) && (
                        <div className="image-preview-container">
                          <img src={fotoPreview} alt="Preview" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* --- Sección de Horarios y Anticipación --- */}
                <div className="amenity-card-section">
                  <div className="section-title">
                    <div className="section-icon"><IoTimeOutline size={22} /></div>
                    Horarios y Bloques
                  </div>
                  
                  <div className="form-grid-2">
                    <div className="premium-group">
                      <label className="premium-label">Horario de Apertura</label>
                      <input type="time" value={horarioApertura} onChange={(e) => setHorarioApertura(e.target.value)} className="premium-input" required />
                    </div>
                    <div className="premium-group">
                      <label className="premium-label">Horario de Cierre</label>
                      <input type="time" value={horarioCierre} onChange={(e) => setHorarioCierre(e.target.value)} className="premium-input" required />
                    </div>
                    <div className="premium-group">
                      <label className="premium-label">Duración por Reserva (minutos)</label>
                      <select value={duracionBloque} onChange={(e) => setDuracionBloque(e.target.value)} className="premium-select" required>
                        <option value={30}>30 minutos</option>
                        <option value={60}>1 hora</option>
                        <option value={90}>1 hora y media</option>
                        <option value={120}>2 horas</option>
                        <option value={180}>3 horas</option>
                        <option value={240}>4 horas</option>
                      </select>
                    </div>
                    <div className="premium-group">
                      <label className="premium-label">Anticipación Mínima (horas)</label>
                      <input type="number" value={horasAnticipacion} onChange={(e) => setHorasAnticipacion(e.target.value)} className="premium-input" min="0" placeholder="0 para ninguna" required />
                    </div>
                  </div>
                </div>

                {/* --- Sección de Capacidad y Límites --- */}
                <div className="amenity-card-section">
                  <div className="section-title">
                    <div className="section-icon"><IoPeopleOutline size={22} /></div>
                    Aforos y Reglas de Uso
                  </div>
                  
                  <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                    <div className="premium-group">
                      <label className="premium-label">Límite de Asistentes (Por Reserva)</label>
                      <input type="number" value={limiteUsuariosPorReserva} onChange={(e) => setLimiteUsuariosPorReserva(e.target.value)} className="premium-input" min="1" required />
                    </div>
                    <div className="premium-group">
                      <label className="premium-label">Aforo Simultáneo (Reservas al mismo tiempo)</label>
                      <input type="number" value={capacidadSimultanea} onChange={(e) => setCapacidadSimultanea(e.target.value)} className="premium-input" min="1" required />
                    </div>
                  </div>

                  <div className="premium-group">
                    <label className="premium-label">Frecuencia Máxima de Uso</label>
                    <div className="inline-limit-row">
                      <span className="inline-text">Máximo</span>
                      <input type="number" value={limiteReservas} onChange={(e) => setLimiteReservas(e.target.value)} className="premium-input" min="1" style={{ width: '80px', padding: '8px 12px' }} />
                      <span className="inline-text">reservas por cada</span>
                      <input type="number" value={limitePeriodoNum} onChange={(e) => setLimitePeriodoNum(e.target.value)} className="premium-input" min="1" style={{ width: '80px', padding: '8px 12px' }} />
                      <select value={limitePeriodoTipo} onChange={(e) => setLimitePeriodoTipo(e.target.value)} className="premium-select" style={{ width: '140px', padding: '8px 12px' }}>
                        <option value="Día">Día(s)</option>
                        <option value="Semana">Semana(s)</option>
                        <option value="Mes">Mes(es)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* --- Sección de Acceso y Finanzas --- */}
                <div className="amenity-card-section">
                  <div className="section-title">
                    <div className="section-icon"><IoWalletOutline size={22} /></div>
                    Acceso y Finanzas
                  </div>
                  
                  <div className="premium-group" style={{ marginBottom: '20px' }}>
                    <label className="premium-label">Derechos de Acceso Exclusivo (Grupos, Torres)</label>
                    
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      {etiquetasAcceso.length === 0 && <span style={{ fontSize: '0.85rem', color: '#16a34a', padding: '6px 0', fontWeight: 'bold' }}>✅ Acceso público (Todos los residentes pueden verla y reservar)</span>}
                      {etiquetasAcceso.map((tag, idx) => (
                          <span key={idx} style={{ background: '#eef2ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px solid #c7d2fe' }}>
                              {tag}
                              <IoClose style={{ cursor: 'pointer', fontSize: '1.2em' }} onClick={() => setEtiquetasAcceso(etiquetasAcceso.filter((_, i) => i !== idx))} />
                          </span>
                      ))}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input
                          type="text"
                          className="premium-input"
                          placeholder="Ej. Torre A, Directiva, VIP..."
                          value={nuevaEtiqueta}
                          onChange={(e) => setNuevaEtiqueta(e.target.value)}
                          onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (nuevaEtiqueta.trim() && !etiquetasAcceso.includes(nuevaEtiqueta.trim())) {
                                      setEtiquetasAcceso([...etiquetasAcceso, nuevaEtiqueta.trim()]);
                                      setNuevaEtiqueta('');
                                  }
                              }
                          }}
                      />
                      <button
                          type="button"
                          className="btn-save-premium"
                          style={{ padding: '0 20px', width: 'auto', background: '#3b82f6', boxShadow: 'none' }}
                          onClick={() => {
                              if (nuevaEtiqueta.trim() && !etiquetasAcceso.includes(nuevaEtiqueta.trim())) {
                                  setEtiquetasAcceso([...etiquetasAcceso, nuevaEtiqueta.trim()]);
                                  setNuevaEtiqueta('');
                              }
                          }}
                      >
                          Añadir Tag
                      </button>
                    </div>
                    <span style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '6px', display: 'block' }}>Si está vacío, la amenidad será pública. Para restringir, añade etiquetas que coincidan con las asignadas al residente.</span>
                  </div>
                  
                  <label className="premium-checkbox-wrapper" style={{ marginBottom: requiereDeposito ? '15px' : '0' }}>
                    <input type="checkbox" checked={requiereDeposito} onChange={(e) => setRequiereDeposito(e.target.checked)} className="premium-checkbox" />
                    <span className="premium-label" style={{ margin: 0, cursor: 'pointer' }}>Esta amenidad requiere depósito en garantía para uso</span>
                  </label>

                  {requiereDeposito && (
                    <div className="premium-group" style={{ marginTop: '0', background: '#f8fafc', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                      <label className="premium-label" style={{ color: '#b45309' }}>Monto del Depósito Exigido ($)</label>
                      <input type="number" min="0" step="1" value={montoDeposito} onChange={(e) => setMontoDeposito(e.target.value)} className="premium-input" placeholder="Ej. 500" required />
                      <span style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '6px', display: 'block' }}>Este monto se le informará al residente al crear la reserva.</span>
                    </div>
                  )}
                </div>

                {/* Acciones */}
                <div className="form-actions-footer">
                  <button type="submit" className="btn-save-premium" disabled={enviando || loadingData}>
                    <IoSaveOutline size={20} />
                    {enviando ? 'Guardando Configuración...' : (isEditMode ? 'Guardar Cambios' : 'Crear Amenidad')}
                  </button>
                </div>
                
              </form>
            </div>
          </div>
        </div>
      </main>

      {successModal.show && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(15, 23, 42, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', textAlign: 'center', animation: 'scaleIn 0.3s ease-out' }}>
            <IoCheckmarkCircleOutline color="#10b981" size={70} style={{ marginBottom: '20px' }} />
            <h2 style={{ margin: '0 0 10px 0', color: '#1e293b', fontSize: '1.5rem', fontWeight: 'bold' }}>¡Operación Exitosa!</h2>
            <p style={{ color: '#64748b', marginBottom: '25px', fontSize: '1rem', lineHeight: '1.5' }}>{successModal.message}</p>
            <button onClick={() => { setSuccessModal({ show: false, message: "" }); navigate('/gestion-amenidades'); }} style={{ width: '100%', padding: '12px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem', boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.4)' }}>
              Aceptar
            </button>
          </div>
        </div>
      )}

      <KondoBottomNav activeRoute="/gestion-amenidades" />
    </div>
  );
}

export default AdminAmenidadesScreen;